import { memo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuraLayout, Layout, PageTitle, AddCardIcon } from "lola-framework-ui-test";

import { FlowHeroMark } from "../../components/FlowHeroMark";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";
import { SEND_ROUTES, TITLE_COLOR } from "../../constants";
import { useComposeStore } from "../../store/useComposeStore";
import { useStore } from "../../store/globalState";
import type { CardStepFields } from "./AddCardPage";

/**
 * Card registration transition (gallery-safe).
 *
 * Opened from the Control Dock: static spinner, no redirects.
 * Reached via Add Card → Billing with router state: arms a **saved**
 * funding instrument (register already happened in live) and returns to
 * Transfer after a short delay. Zero network.
 */
export const AddCardProcessingPage = memo(() => {
  const { t } = useTranslation("send");
  const { theme } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { setFundingInstrument } = useComposeStore();

  const colorConfig = theme.whiteView;
  const { dropzoneColors, backgroundIcon } = colorConfig;

  useEffect(() => {
    const card = (location.state as { card?: CardStepFields } | null)?.card;
    // Gallery dock opens this screen with no state — stay on the static view.
    if (!card) return;

    const digits = card.cardNumber.replace(/\D/g, "");
    const last4 = digits.slice(-4) || "4242";
    const brand = digits.startsWith("4") ? "VISA" : "MASTERCARD";

    setFundingInstrument({
      paymentMethodId: `pm_gallery_new_${last4}`,
      brand,
      last4,
    });

    const timer = setTimeout(() => {
      navigate(SEND_ROUTES.TRANSFER, { replace: true });
    }, 1500);

    return () => clearTimeout(timer);
  }, [location.state, navigate, setFundingInstrument]);

  return (
    <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
      <Layout.Content className="landing-section flow-hero-section">
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />
        <section className="flow-hero">
          <FlowHeroMark
            showSpinner
            dropzoneColors={dropzoneColors}
            backgroundIcon={backgroundIcon}
          >
            <AddCardIcon size={60} colors={dropzoneColors} />
          </FlowHeroMark>
          <section className="landing-container">
            <PageTitle
              highlight={t("card.addingCard")}
              highlightColor={TITLE_COLOR}
              secudnary={t("card.pleaseWait")}
              secudnaryColor={TITLE_COLOR}
            />
          </section>
        </section>
      </Layout.Content>
    </AuraLayout>
  );
});
